/**
 * KB-2134 — authoring-time regUrl validation for the admin/server offer-prep seam.
 *
 * This is the pure TS port of the `.cjs` pipeline validators (KB-2106 / KB-2128 pt2) that live in
 * `scripts/camp-data/_reconcile/observedToOffersLib.cjs`. It exists so the ADMIN/SERVER offer-prep
 * path (prepareOfferDataServer — bulk-sessions, publishOffersForLocation, session-ai applyChanges,
 * shared-programs sync, collision updates) refuses to PERSIST a vendor LISTING / scrape-SEED URL as
 * an offer's per-session `regUrl`, exactly as the pipeline already does. Same predicate, so the two
 * paths can never disagree about what a "listing" is.
 *
 * The RLT bulk-onboarding incident stamped `…/coursestorm.com/category/summer-camps` (a CourseStorm
 * category listing) as the `regUrl` of all 32 offers. A bare category/listing page — or the
 * location's own scrape seed (`variationData.dataFetchUrl`) — is NEVER a real "Register here" link,
 * so it must not be persisted as one on CREATE.
 *
 * Behavior is byte-for-byte faithful to the `.cjs` source:
 *  - `isListingOrSummaryRegUrl` keys ONLY on a TERMINAL `category` path segment (the CourseStorm
 *    listing convention; KB-2143 added the terminal requirement). A real `/course/<id>` detail page,
 *    a bare domain, a third-party checkout deep link (query/id-bearing), or a deep link that merely
 *    passes THROUGH a `category` segment (Acuity) is NOT a listing.
 *  - `sanitizeAuthoredRegUrl` drops (→ null) a listing/summary URL or an exact scrape-seed match; an
 *    absent value passes through unchanged; a valid deep link is returned untouched.
 */

/**
 * KB-2106 — a LISTING / SUMMARY registration page, as opposed to a per-session registration link.
 * The concrete, shipped case is a CourseStorm category index (`…/coursestorm.com/category/summer-camps`),
 * which a course's `regUrl` sometimes freezes onto instead of its `…/course/4218-…` detail page.
 *
 * ⚠ It is NOT the negation of a "deep link" check: `/category/summer-camps` has two path segments and
 * would read as deep by a depth scan. This predicate keys specifically on a `category` path segment
 * (the listing convention). Deliberately TARGETED and conservative: a real `/course/<id>` detail page
 * or a third-party checkout deep link (acceptiva `?cst=…`, eventbrite `/e/…-tickets-<id>`) is NEVER
 * misclassified as a listing.
 *
 * ⚠ KB-2143 — the `category` segment must be TERMINAL (nothing after it, or exactly one slug after
 * it). Keying on the segment appearing ANYWHERE misread Acuity Scheduling's per-appointment deep
 * link — `…as.me/schedule/<id>/category/Art%2520Camps/appointment/<id>/calendar/<id>` — as a
 * listing, because Acuity puts `category` mid-path. The distinction is set-vs-instance: a listing
 * NAMES a set and the path stops there; a deep link continues past `category` with the segments
 * that identify one session. Chosen over an "is the following segment id-bearing?" test because
 * this needs no heuristic about what an id looks like. Measured against all 19,819 prod offers
 * (9,438 with a regUrl): 12 dropped before, 11 after — the RLT rows are untouched and only the one
 * Acuity false positive is released.
 */
export function isListingOrSummaryRegUrl(url: string | null | undefined): boolean {
  const s = String(url == null ? "" : url).trim();
  if (!s) return false;
  let parsed: URL;
  try {
    parsed = new URL(s);
  } catch {
    return false;
  }
  const segs = parsed.pathname.split("/").filter(Boolean);
  return segs.some((seg, i) => seg.toLowerCase() === "category" && segs.length - 1 - i <= 1);
}

/**
 * Host + path + QUERY of a URL, lowercased, trailing slash on the PATH normalized — for a GENUINE
 * seed-equality test. The query is DELIBERATELY INCLUDED: a real per-session deep link is frequently
 * the seed's base path PLUS a distinguishing query (acceptiva `?cst=…`, a webtrac session id), and
 * that query is exactly what makes it NOT the seed — stripping it would false-positive those valid
 * links as "equals-seed" and drop them. Two URLs are "equal" only when host, path, AND query match.
 * Only the trailing slash on the path and letter-case are normalized, so `…/x` == `…/x/`. Falls back
 * to the trimmed lowercased string (fragment/whitespace normalized) on a parse failure.
 */
function normSeedUrlForCompare(u: string | null | undefined): string {
  const s = String(u == null ? "" : u).trim().toLowerCase();
  if (!s) return "";
  try {
    const p = new URL(s);
    return `${p.host}${p.pathname.replace(/\/+$/, "")}${p.search}`;
  } catch {
    return s.replace(/\/+$/, "");
  }
}

export interface SanitizeAuthoredRegUrlResult {
  regUrl: string | null;
  dropped: boolean;
  reason: string | null;
}

/**
 * KB-2128 (part 2) — the PURE authoring-time regUrl validator. Given a candidate regUrl (and, if
 * available, the location's scrape seed), decide whether it may be PERSISTED as an offer's regUrl.
 *
 * Refuses (drops to null) when the candidate is a listing/summary page (isListingOrSummaryRegUrl,
 * KB-2106) OR equals the seed (`variationData.dataFetchUrl`) — neither is a per-session registration
 * link. An ABSENT regUrl (null/""/whitespace) passes through unchanged (nothing to validate). A VALID
 * deep link is returned untouched. PURE: no IO, and it never mutates its inputs.
 */
export function sanitizeAuthoredRegUrl(
  regUrl: string | null | undefined,
  { seedUrl }: { seedUrl?: string | null } = {}
): SanitizeAuthoredRegUrlResult {
  const s = String(regUrl == null ? "" : regUrl).trim();
  if (!s) return { regUrl: regUrl == null ? null : regUrl, dropped: false, reason: null };
  if (isListingOrSummaryRegUrl(s)) return { regUrl: null, dropped: true, reason: "listing-or-summary" };
  const seed = normSeedUrlForCompare(seedUrl);
  if (seed && normSeedUrlForCompare(s) === seed) return { regUrl: null, dropped: true, reason: "equals-seed" };
  return { regUrl: regUrl == null ? null : regUrl, dropped: false, reason: null };
}
